import React from 'react'
import { useIntl } from 'react-intl'
import Image from 'next/image'

import FeaturesFooter from '@/components/sections/FeaturesFooter'
import { Column, Layout, Row, Rows, Section } from '@/components/layout'
import FeaturePageHeader from '@/components/sections/FeaturePageHeader'
import { Link, Text } from '@/components/common'
import routes from '@/routes'
import CustomerSupport from '@/components/sections/CustomerSupport'
import transactionStreamImg from '@/assets/transaction-streaming/transaction-stream.png'
import selectReceiptImg from '@/assets/transaction-streaming/select-receipt.png'
import matchingReconciliationImg from '@/assets/transaction-streaming/matching-reconciliation.png'
import transactionStreamingSoftwareImg from '@/assets/transaction-streaming/transaction-streaming-software.png'

const pageId = 'transactionStreaming'

const getMessageId = id => `pages.${pageId}.${id}`

export default function TransactionStreaming() {
  const { formatMessage } = useIntl()
  const getPageMessage = (id) => formatMessage({ id: getMessageId(id) })

  return (
    <Layout.Content>

      <FeaturePageHeader
        description={getPageMessage('header.description')}
        heroProps={{
          src: transactionStreamingSoftwareImg,
          alt: 'transaction-streaming-software',
          width: 700,
          style: { height: 'auto' },
        }}
        subtitle={getPageMessage('header.subtitle')}
        title={getPageMessage('header.title')}
      />

      <Section>
        <Row>
          <Column center size={50}>
            <Text.H2>Automated Document Matching for Hassle-Free Reconciliation</Text.H2>
            <Text.P>Neat automatically suggests matches for your transactions, minimizing manual work and ensuring your records are always accurate.</Text.P>
          </Column>
        </Row>
      </Section>

      <Section bgSecondary>
        <Row>
          <Column center size={60}>
            <Image
              alt="transaction-stream"
              src={transactionStreamImg}
              style={{ height: 'auto' }}
            />
          </Column>
          <Column center size={40}>
            <Rows className="w-full max-w-400">
              <Text.H2>Consolidate Your Data</Text.H2>
              <Text.P><strong>Manage all of your transactions and documents in one place.</strong> Paper receipts, bank transactions, invoices and email data are all consolidated for one-screen reconciliation. With Neat you won’t have to worry about missing transactions.</Text.P>
            </Rows>
          </Column>
        </Row>
      </Section>

      <Section>
        <Row>
          <Column center size={50}>
            <Rows className="w-full max-w-450">
              <Text.H2>Minimize Manual Data Entry</Text.H2>
              <Text.P><strong>Use Neat to eliminate your paper mess.</strong> Tying transactions to documents in one screen eliminates the stress of managing paper. Neat automatically suggests matches based on your transactions giving you more time to focus on business decisions.</Text.P>
            </Rows>
          </Column>
          <Column center size={50}>
            <Image
              alt="select-receipt"
              className="max-w-500"
              src={selectReceiptImg}
              style={{ height: 'auto' }}
            />
          </Column>
        </Row>
      </Section>

      <Section bgSecondary>
        <Row>
          <Column size={50}>
            <Image
              alt="matching-reconciliation"
              className="shadow-card"
              src={matchingReconciliationImg}
              style={{ height: 'auto' }}
            />
          </Column>
          <Column center size={50}>
            <Rows className="w-full max-w-500">
              <Text.H2>Reconcile Receipts and Invoices</Text.H2>
              <Text.P><strong>Match, categorize and reconcile with ease.</strong> Have confidence that all of your transactions are centralized and accurate. No need to reconcile off of a bank statement, every transaction source is accounted for in Neat.</Text.P>
              <div>
                <Link
                  iconProps={{ name: 'chevron-right', position: 'right' }}
                  messageId={getMessageId('reconcile.cta')}
                  size="large"
                  to={routes.taxPrep}
                  variant="primary"
                />
              </div>
            </Rows>
          </Column>
        </Row>
      </Section>

      <CustomerSupport
        bgImage="/track-receipts/track-receipts-banner.webp"
        description="Ensure every expense is accounted for and categorized correctly—Neat makes it easy to reconcile receipts, invoices, and bank transactions, helping you maximize tax deductions and keep more of your hard-earned money."
        title="Maximize Deductions with Automatic Reconciliation"
      />

      <FeaturesFooter />

    </Layout.Content>
  )
}

TransactionStreaming.layoutProps = {
  pageId,
}
